import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Printer, Download, CheckCircle2 } from "lucide-react";
import { LogoMark } from "../../components/Logo";
import { Button } from "../../components/ui/button";
import { LoadingBlock, EmptyState } from "../../components/app/ui";
import { useAuth } from "../../context/AuthContext";
import { foodApi, ridesApi, inr, formatDate, titleCase } from "../../lib/api";

function Row({ label, value, bold = false, tone = "" }) {
  return (
    <div className={`flex justify-between py-1.5 text-sm ${bold ? "border-t border-ink/15 pt-3 text-base font-bold" : ""} ${tone}`}>
      <span className={bold ? "" : "text-ink-soft"}>{label}</span><span>{value}</span>
    </div>
  );
}

export default function ReceiptPage() {
  const { kind, id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    setData(null); setError("");
    const p = kind === "ride" ? ridesApi.get(id) : foodApi.order(id);
    p.then(setData).catch(() => setError("Receipt not found"));
  }, [kind, id]);

  const print = () => window.print();
  const back = kind === "ride" ? "/app/rides" : "/app/orders";

  if (error) return <EmptyState title={error} action={{ to: back, label: "Back" }} />;
  if (!data) return <LoadingBlock rows={3} />;

  const isRide = kind === "ride";
  const number = (isRide ? data.ride_id : data.order_id).replace(/^(rid|ord)_/, "").slice(0, 8).toUpperCase();
  const paid = data.payment_status === "paid";
  const date = isRide ? data.completed_at || data.created_at : data.delivered_at || data.created_at;

  return (
    <>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3 print:hidden">
        <Link to={back} className="inline-flex items-center text-sm font-medium text-ink-soft hover:text-brand" data-testid="receipt-back"><ArrowLeft className="mr-1.5 h-4 w-4" /> Back to {isRide ? "rides" : "orders"}</Link>
        <div className="flex gap-2">
          <Button variant="outline" onClick={print} className="rounded-full" data-testid="receipt-print"><Printer className="mr-2 h-4 w-4" /> Print</Button>
          <Button asChild className="rounded-full bg-brand text-white hover:bg-brand-600" data-testid="receipt-download"><a href={isRide ? ridesApi.invoicePdfUrl(id) : foodApi.invoicePdfUrl(id)} target="_blank" rel="noreferrer"><Download className="mr-2 h-4 w-4" /> Download {data.gst?.registered ? "tax invoice" : "PDF"}</a></Button>
        </div>
      </div>
      <article id="receipt" className="kt-receipt mx-auto max-w-2xl rounded-3xl border border-surface-300 bg-white p-8 shadow-sm print:max-w-none print:rounded-none print:border-0 print:p-0 print:shadow-none" data-testid="receipt">
        <header className="flex items-start justify-between border-b border-ink/15 pb-6">
          <div className="flex items-center gap-3">
            <LogoMark className="h-11 w-11" />
            <div><p className="text-lg font-bold text-ink">KohimaTaxi &amp; Delivery</p><p className="text-xs text-ink-soft">Seamless rides. Fresh deliveries.</p><p className="text-xs text-ink-soft">Kohima STPI, Directorate of IT &amp; Communication, Thizama Road, Kohima, Nagaland - 797001</p><p className="text-xs text-ink-soft">support@kohimataxi.com · +91 9545897697 · kohimataxi.com</p></div>
          </div>
          <div className="text-right">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-brand">{isRide ? "Ride receipt" : "Order receipt"}</p>
            <p className="mt-1 text-sm font-semibold text-ink">#{number}</p>
            <p className="text-xs text-ink-soft">{formatDate(date)}</p>
          </div>
        </header>

        <section className="grid gap-6 py-6 sm:grid-cols-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-ink-muted">Billed to</p>
            <p className="mt-1 text-sm font-semibold text-ink">{isRide ? data.rider_name : data.customer_name}</p>
            <p className="text-sm text-ink-soft">{(isRide ? data.rider_phone : data.customer_phone) || user?.email}</p>
            {!isRide && <p className="mt-1 text-sm text-ink-soft">{data.delivery_address}</p>}
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-ink-muted">{isRide ? "Trip" : "Restaurant"}</p>
            {isRide ? (
              <div className="mt-1 space-y-1 text-sm">
                <p className="flex gap-2"><span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-graphite" /><span className="text-ink">{data.pickup_address}</span></p>
                <p className="flex gap-2"><span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-brand" /><span className="text-ink">{data.drop_address}</span></p>
                <p className="text-ink-soft">{data.distance_km} km · {data.duration_min} min · {titleCase(data.vehicle_type)}</p>
              </div>
            ) : (
              <div className="mt-1 text-sm"><p className="font-semibold text-ink">{data.restaurant_name}</p>{data.driver && <p className="text-ink-soft">Delivered by {data.driver.name}</p>}</div>
            )}
            {isRide && data.driver && <p className="mt-2 text-sm text-ink-soft">Driver: {data.driver.name} · {data.driver.vehicle_model} · {data.driver.plate}</p>}
          </div>
        </section>

        <section className="border-t border-ink/15 pt-4">
          {isRide ? (
            <>
              {data.fare_breakdown ? (
                <div data-testid="ride-fare-breakdown">
                  <Row label={`Base fare (${titleCase(data.vehicle_type)}, incl. first 1 km)`} value={inr(data.fare_breakdown.base)} />
                  {data.fare_breakdown.distance_charge > 0 && <Row label={`Distance (${data.fare_breakdown.charged_km} km × ${inr(data.fare_breakdown.per_km)})`} value={inr(data.fare_breakdown.distance_charge)} />}
                  {data.fare_breakdown.time_charge > 0 && <Row label={`Time (${data.fare_breakdown.duration_min} min × ${inr(data.fare_breakdown.per_min)})`} value={inr(data.fare_breakdown.time_charge)} />}
                  {data.fare_breakdown.surge_amount > 0 && <Row label={`Surge ×${data.fare_breakdown.surge_multiplier}${data.fare_breakdown.surge_reasons?.length ? ` (${data.fare_breakdown.surge_reasons.join(", ")})` : ""}`} value={inr(data.fare_breakdown.surge_amount)} />}
                </div>
              ) : (
                <Row label={`Base fare (${titleCase(data.vehicle_type)})`} value={inr(data.base_fare ?? data.fare)} />
              )}
              {data.ac_applied && data.ac_charge > 0 && <Row label="Air Conditioning (+20%)" value={inr(data.ac_charge)} />}
              {data.distance_fee > 0 && <Row label="Far-pickup fee" value={inr(data.distance_fee)} />}
              {data.waiting_charge > 0 && <Row label={`Waiting charge (${data.waiting_min || 0} min)`} value={inr(data.waiting_charge)} />}
              {data.applied_cancel_balance > 0 && <Row label="Previous cancellation fee" value={inr(data.applied_cancel_balance)} />}
              {data.discount > 0 && <Row label={`Promo ${data.promo?.code || ""}`} value={`-${inr(data.discount)}`} tone="text-graphite" />}
              <Row label="Total" value={inr(data.fare)} bold />
            </>
          ) : (
            <>
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs uppercase tracking-wider text-ink-muted"><th className="pb-2 font-semibold">Item</th><th className="pb-2 text-center font-semibold">Qty</th><th className="pb-2 text-right font-semibold">Price</th><th className="pb-2 text-right font-semibold">Amount</th></tr></thead>
                <tbody className="divide-y divide-ink/10">
                  {data.items.map((i, idx) => <tr key={idx}><td className="py-2 text-ink">{i.name}</td><td className="py-2 text-center text-ink-soft">{i.qty}</td><td className="py-2 text-right text-ink-soft">{inr(i.price)}</td><td className="py-2 text-right font-medium text-ink">{inr(i.line_total ?? i.price * i.qty)}</td></tr>)}
                </tbody>
              </table>
              <div className="mt-4 ml-auto max-w-xs">
                <Row label="Subtotal" value={inr(data.subtotal)} />
                {data.packaging_fee > 0 && <Row label="Packaging fee" value={inr(data.packaging_fee)} />}
                <Row label="Delivery fee" value={inr(data.delivery_fee)} />
                {data.gst?.registered ? (
                  <>
                    <Row label="CGST (2.5%)" value={inr(data.gst.cgst)} />
                    <Row label="SGST (2.5%)" value={inr(data.gst.sgst)} />
                  </>
                ) : (data.tax > 0 && <Row label="Tax" value={inr(data.tax)} />)}
                {data.tip > 0 && <Row label="Tip" value={inr(data.tip)} />}
                {data.discount > 0 && <Row label={`Promo ${data.promo?.code || ""}`} value={`-${inr(data.discount)}`} tone="text-graphite" />}
                <Row label="Total" value={inr(data.total)} bold />
              </div>
              {data.gst?.registered && data.gst?.gstin && <p className="mt-2 text-right text-xs text-ink-soft">GSTIN: {data.gst.gstin}</p>}
            </>
          )}
        </section>

        <footer className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-ink/15 pt-5 text-xs text-ink-soft">
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 font-semibold ${paid ? "bg-graphite text-white" : "bg-surface-200 text-ink-soft"}`}>{paid && <CheckCircle2 className="h-3.5 w-3.5" />}{paid ? "Paid" : "Payment pending"}</span>
            <span>{data.payment_method ? `via ${data.payment_method.toUpperCase() === "UPI" ? "UPI" : titleCase(data.payment_method)}` : ""}</span>
            <span>· Status: {titleCase(data.status)}</span>
          </div>
          <p>Thank you for moving with KohimaTaxi. This is a computer-generated receipt.</p>
        </footer>
      </article>
    </>
  );
}
